import { escapeUserText } from "../../utils/mentions";
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Calendar, Camera, ChevronRight, MapPin, Star } from "lucide-react";
import { EventMediaDisplay } from "./eventMediaDisplay";
import EditCoolActivity from "./EditCoolActivity";
import "./Alleventlist.css";

const createLinkifiedText = (text = "") =>
  escapeUserText(text).replace(
    /(https?:\/\/[^\s]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );

const formatEventDate = (value) => {
  if (!value) return "TBA";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatLocation = (event) => {
  const location = (event.location || "").trim();
  const country = (event.country || "").trim();
  if (location && country && location.toLowerCase() !== country.toLowerCase()) {
    return `${location}, ${country}`;
  }
  return location || country || "TBA";
};

const resolveMediaSrc = (value, apiUrl) => {
  if (!value) return "";
  if (value.startsWith("//")) return `https:${value}`;
  if (/^https?:\/\//i.test(value)) return value;
  return `${apiUrl}/uploads/events/${value.replace(/^\/+/, "")}`;
};

const parseMediaItems = (event, apiUrl) => {
  if (!event?.media) return [];

  let raw = event.media;
  if (typeof raw === "string" && raw.trim().startsWith("[")) {
    try {
      raw = JSON.parse(raw);
    } catch {
      /* keep string */
    }
  }

  const parts = Array.isArray(raw)
    ? raw
    : String(raw)
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);

  if (parts.length <= 1) {
    return event.media
      ? [
          {
            src: resolveMediaSrc(event.media, apiUrl),
            type: event.media_type || "image",
          },
        ]
      : [];
  }

  return parts.map((part) => ({
    src: resolveMediaSrc(part, apiUrl),
    type: /\.(mp4|webm|mov|m4v)(\?|$)/i.test(part) ? "video" : "image",
  }));
};

const truncateText = (text = "", limit = 140) => {
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trim()}...`;
};

const EventMediaPanel = ({ event, apiUrl }) => {
  const items = parseMediaItems(event, apiUrl);
  const imageItems = items.filter((item) => item.type === "image");
  const showGrid = imageItems.length >= 4;

  if (!event?.media) {
    return <div className="ea-media-empty">No media</div>;
  }

  if (showGrid) {
    return (
      <>
        <div className="ea-media-grid">
          {imageItems.slice(0, 4).map((item, index) => (
            <img
              key={`${item.src}-${index}`}
              src={item.src}
              alt={`${event.event_name || "Event"} ${index + 1}`}
            />
          ))}
        </div>
        <span className="ea-photo-badge">
          <Camera size={14} strokeWidth={2.4} />
          {imageItems.length} Photos
        </span>
      </>
    );
  }

  return (
    <>
      <div className="ea-media-inner">
        <EventMediaDisplay
          event={event}
          apiUrl={apiUrl}
          mediaStyle={{ width: "100%", height: "100%", objectFit: "cover" }}
          noMediaContent={<div className="ea-media-empty">No media</div>}
        />
      </div>
      {event.media_type === "image" && (
        <span className="ea-photo-badge">
          <Camera size={14} strokeWidth={2.4} />
          1 Photo
        </span>
      )}
    </>
  );
};

const AllEvents = ({ user }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCards, setVisibleCards] = useState({});
  const [editingEvent, setEditingEvent] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;
  const cardsRef = useRef([]);

  const loadEvents = () => {
    fetch(`${API_URL}/get_event_activities.php`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setEvents(data.data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching events:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute("data-id");
            setVisibleCards((prev) => ({ ...prev, [id]: true }));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    cardsRef.current.forEach((card) => card && observer.observe(card));
    return () => observer.disconnect();
  }, [events]);

  if (loading) {
    return <p className="ea-loading">Loading events...</p>;
  }

  return (
    <div className="ea-page">
      <header className="ea-header">
        <div className="ea-header-icon" aria-hidden="true">
          <Calendar size={22} strokeWidth={2.2} />
        </div>
        <h2 className="ea-title">All Event Activities</h2>
        <p className="ea-subtitle">
          Discover and explore our latest events and activities
        </p>
        <div className="ea-header-rule" />
      </header>

      {events.length === 0 ? (
        <p className="ea-empty">No events found.</p>
      ) : (
        <div className="ea-list">
          {events.map((event, index) => {
            const isFeatured = index === 0;
            const isVisible = visibleCards[event.id];
            const description = truncateText(event.description || "", 160);

            return (
              <article
                key={event.id}
                data-id={event.id}
                ref={(el) => {
                  cardsRef.current[index] = el;
                }}
                className={`ea-card${isVisible ? " is-visible" : ""}`}
              >
                <div className="ea-media">
                  <EventMediaPanel event={event} apiUrl={API_URL} />
                </div>

                <div className="ea-body">
                  <span className="ea-badge">
                    {isFeatured ? (
                      <>
                        <Star size={13} strokeWidth={2.4} fill="currentColor" />
                        Featured Event
                      </>
                    ) : (
                      <>
                        <Calendar size={13} strokeWidth={2.4} />
                        Open Enrollment
                      </>
                    )}
                  </span>

                  <h3 className="ea-event-title">{event.event_name}</h3>

                  <p
                    className="ea-desc"
                    dangerouslySetInnerHTML={{
                      __html: createLinkifiedText(description),
                    }}
                  />

                  <hr className="ea-divider" />

                  <div className="ea-meta">
                    <div className="ea-meta-row">
                      <span className="ea-meta-icon" aria-hidden="true">
                        <Calendar size={16} strokeWidth={2.2} />
                      </span>
                      <div className="ea-meta-copy">
                        <span className="ea-meta-label">Date</span>
                        <span className="ea-meta-value">
                          {formatEventDate(event.event_date)}
                        </span>
                      </div>
                    </div>

                    <div className="ea-meta-row">
                      <span className="ea-meta-icon" aria-hidden="true">
                        <MapPin size={16} strokeWidth={2.2} />
                      </span>
                      <div className="ea-meta-copy">
                        <span className="ea-meta-label">Location</span>
                        <span className="ea-meta-value">
                          {formatLocation(event)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="ea-actions">
                    <Link
                      to={`/events/${event.id}`}
                      className={`ea-btn ${
                        isFeatured ? "ea-btn-primary" : "ea-btn-outline"
                      }`}
                    >
                      View Details
                      <ChevronRight size={16} strokeWidth={2.5} />
                    </Link>
                    {user?.role === "admin" && (
                      <button
                        type="button"
                        className="ea-btn ea-btn-edit"
                        onClick={() => setEditingEvent(event)}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <p className="ea-footer">
        <Calendar size={15} strokeWidth={2} />
        More events and activities coming your way. Stay tuned and don&apos;t
        miss out!
      </p>

      {user?.role === "admin" && editingEvent && (
        <EditCoolActivity
          event={editingEvent}
          onClose={() => setEditingEvent(null)}
          onUpdated={loadEvents}
        />
      )}
    </div>
  );
};

export default AllEvents;
